import { memo, useCallback, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { sendEarlyStageRejectionContact } from "../../api/earlyStageMembershipApi";

const DEFAULT_SUBJECT =
  "Early Stage Young Professional (£2/month) – rejected application enquiry";

const SUPPORT_EMAIL = "enquiries@smbf.global";

export interface EarlyStageRejectionContactModalProps {
  show: boolean;
  onHide: () => void;
  memberEmail: string;
  memberName: string;
}

function EarlyStageRejectionContactModalInner({
  show,
  onHide,
  memberEmail,
  memberName,
}: EarlyStageRejectionContactModalProps) {
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [feedback, setFeedback] = useState("");
  const [submitError, setSubmitError] = useState("");

  const resetForm = useCallback(() => {
    setSubject(DEFAULT_SUBJECT);
    setFeedback("");
    setSubmitError("");
  }, []);

  const handleHide = useCallback(() => {
    resetForm();
    onHide();
  }, [onHide, resetForm]);

  const { mutate: sendContact, isPending } = useMutation({
    mutationFn: sendEarlyStageRejectionContact,
    onSuccess: (result) => {
      const recipient = result.recipientEmail?.trim() || SUPPORT_EMAIL;
      toast.success(`Your message was sent to ${recipient}.`);
      handleHide();
    },
    onError: () => {
      setSubmitError("Failed to send your message. Please try again later.");
    },
  });

  const canSend = useMemo(
    () =>
      subject.trim().length > 0 &&
      feedback.trim().length >= 10 &&
      memberEmail.trim().length > 0,
    [subject, feedback, memberEmail],
  );

  const handleSend = () => {
    setSubmitError("");
    if (!canSend) {
      setSubmitError(
        "Please enter a subject and your feedback (at least 10 characters).",
      );
      return;
    }

    sendContact({
      feedback: feedback.trim(),
      subject: subject.trim(),
    });
  };

  return (
    <Modal show={show} onHide={handleHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Contact SMBF</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">
          Your message will be sent to{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. Your account
          details are attached for the admin team only (not shown here).
        </p>

        <Alert variant="light" className="small border mb-3">
          You are contacting us about your rejected Young Professional
          (£2/month) Early Stage application. Please share your feedback below.
        </Alert>

        <MemberSummaryFields memberEmail={memberEmail} memberName={memberName} />

        <Form.Group className="mb-3" controlId="esRejectionContactSubject">
          <Form.Label className="fw-semibold">Email subject</Form.Label>
          <Form.Control
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={200}
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="esRejectionContactFeedback">
          <Form.Label className="fw-semibold">Feedback</Form.Label>
          <Form.Control
            as="textarea"
            rows={6}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Please describe your feedback or question about the rejection…"
          />
          <Form.Text className="text-muted">
            Minimum 10 characters. The admin team receives a branded email from
            our system.
          </Form.Text>
        </Form.Group>

        {submitError && (
          <Alert variant="danger" className="py-2 small mb-0">
            {submitError}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleHide} disabled={isPending}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSend} disabled={isPending || !canSend}>
          {isPending ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" aria-hidden />
              Sending…
            </>
          ) : (
            "Send message"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function MemberSummaryFields({
  memberEmail,
  memberName,
}: {
  memberEmail: string;
  memberName: string;
}) {
  return (
    <div className="row g-3 mb-3">
      <div className="col-md-6">
        <Form.Group>
          <Form.Label className="small text-muted mb-1">Your email</Form.Label>
          <Form.Control
            plaintext
            readOnly
            className="fw-semibold text-break"
            value={memberEmail || "—"}
          />
        </Form.Group>
      </div>
      <div className="col-md-6">
        <Form.Group>
          <Form.Label className="small text-muted mb-1">Your name</Form.Label>
          <Form.Control
            plaintext
            readOnly
            className="fw-semibold"
            value={memberName || "—"}
          />
        </Form.Group>
      </div>
    </div>
  );
}

export default memo(EarlyStageRejectionContactModalInner);
